import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button
} from '@mui/material';
import {
  useCreateOrgGroupAssignment,
  useDeleteOrgGroupAssignment,
  useGetOrgGroupAssignments
} from '../../api/mutategroupassignment.js';
import UserProfileContext from '../../context/userprofilecontext.js';

const FormOrgGroupAssignment = () => {
  // local state to hold the UI’s version of assignments
  const [localAssignments, setLocalAssignments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createGroupAssignment = useCreateOrgGroupAssignment();
  const deleteGroupAssignment = useDeleteOrgGroupAssignment();

  const userProfileContext = useContext(UserProfileContext);

  const orgID = userProfileContext.userProfile?.currentOrg?.orgID;
  const currentUserID = userProfileContext.userProfile?.currentUser?.userID;


  const {
    data: currentAssignmentsData,
    isLoading: isFetchingAssignments,
    error: fetchError,
    mutate
  } = useGetOrgGroupAssignments(orgID);

  // When the SWR data loads, initialize the local state.
  useEffect(() => {
    if (currentAssignmentsData) {
      // Make a deep copy so we can edit locally without affecting the SWR data directly.
      setLocalAssignments(JSON.parse(JSON.stringify(currentAssignmentsData)));
    }
  }, [currentAssignmentsData]);

  if (isFetchingAssignments || !localAssignments) {
    return <div>Loading...</div>;
  }

  if (fetchError) {
    return (
      <div style={{ marginTop: '1rem', color: 'red' }}>
        Failed to load group assignments. Please try again.
      </div>
    );
  }

  // Extract unique group names from the local assignments.
  const allGroups = new Set();
  Object.values(localAssignments).forEach(({ groups }) => {
    if (groups && typeof groups === 'object') {
      Object.keys(groups).forEach(groupName => allGroups.add(groupName));
    }
  });
  const groupNames = Array.from(allGroups);

  // Sort the group names alphabetically
  groupNames.sort(); // This will sort the array in place

  // Update local state immediately when a checkbox is toggled.
  const handleCheckboxChange = (event, userID, groupName) => {
    const isChecked = event.target.checked;
    setLocalAssignments(prev => ({
      ...prev,
      [userID]: {
        ...prev[userID],
        groups: {
          ...prev[userID].groups,
          [groupName]: isChecked,
        }
      }
    }));
  };

  // On submit, compare the local state to the original data to determine changes.
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const assignmentsToAdd = [];
    const assignmentsToRemove = [];

    // Loop through each user and each group.
    for (const userID in localAssignments) {
      const localGroups = localAssignments[userID].groups || {};
      const originalGroups = currentAssignmentsData[userID].groups || {};
      for (const groupName of groupNames) {
        const localValue = localGroups[groupName] || false;
        const originalValue = originalGroups[groupName] || false;
        if (localValue && !originalValue) {
          assignmentsToAdd.push({ userID, groupName: groupName });
        } else if (!localValue && originalValue) {
          assignmentsToRemove.push({ userID, groupName: groupName });
        }
      }
    }

    try {
      if (assignmentsToAdd.length > 0) {
        console.log('Adding group assignments:', assignmentsToAdd);
        await createGroupAssignment(orgID, assignmentsToAdd);
      }
      if (assignmentsToRemove.length > 0) {
          console.log('Removing group assignment:', assignmentsToRemove);
          await deleteGroupAssignment(orgID, assignmentsToRemove);
      }
      // After successful updates, revalidate the SWR cache.
      mutate();
    } catch (err) {
      console.error("Error updating group assignments", err);
      setError('Failed to update group assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            {groupNames.map(groupName => (
              <TableCell key={groupName}>{groupName}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(localAssignments).map(([userID, { name, groups }]) => (
            <TableRow key={userID}>
              <TableCell>{name}</TableCell>
              {groupNames.map(groupName => {
                const isChecked = groups?.[groupName] || false;
                const isDisabled = userID === currentUserID && groupName === 'Org Admins';
                return (
                  <TableCell key={`${userID}-${groupName}`}>
                    <Checkbox
                      checked={isChecked}
                      onChange={(event) =>
                        handleCheckboxChange(event, userID, groupName)
                      }
                      disabled={isDisabled}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {error && (
        <div style={{ marginTop: '1rem', color: 'red' }}>{error}</div>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={loading}
        style={{ marginTop: '1rem' }}
      >
        Save
      </Button>
    </div>
  );
};

FormOrgGroupAssignment.propTypes = {
  orgID: PropTypes.string.isRequired,
  currentUserID: PropTypes.string.isRequired
};

export default FormOrgGroupAssignment;